import ExcelJS from "exceljs";
import {
  WithdrawOfferRow,
  TradeConfirmationRow,
  DetailedWithdrawRequestRow,
  PurchaseHistoryRow,
  BankStatementRow,
  SoldStockRow,
  BondMetadataRow,
  ChargeRow,
  BondDistributionFile,
  BondDistributionRow,
} from "./types";
import {
  parseCellString,
  parseCellNumber,
  ceilTo2Decimals,
  excelDateToString,
  isExcelDate,
  sanitizeEmail,
  normalizeBondName,
} from "./utils";

const parseCsvTextRows = (csvText: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < csvText.length; i++) {
    const ch = csvText[i];
    const next = csvText[i + 1];

    if (ch === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (ch === "," && !inQuotes) {
      row.push(current.trim());
      current = "";
      continue;
    }

    if ((ch === "\n" || ch === "\r") && !inQuotes) {
      if (ch === "\r" && next === "\n") i++;
      row.push(current.trim());
      if (row.some((cell) => cell !== "")) rows.push(row);
      row = [];
      current = "";
      continue;
    }

    current += ch;
  }

  row.push(current.trim());
  if (row.some((cell) => cell !== "")) rows.push(row);
  return rows;
};

// Parse withdraw offer Excel file
export const parseWithdrawOffer = async (file: File): Promise<WithdrawOfferRow[]> => {
  const workbook = new ExcelJS.Workbook();
  const buffer = await file.arrayBuffer();
  await workbook.xlsx.load(buffer);

  const worksheet = workbook.worksheets[0];
  const stocks: WithdrawOfferRow[] = [];

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;

    const assetType = parseCellString(row.getCell(1).value);
    if (assetType !== "Stock") return;

    const symbol = parseCellString(row.getCell(2).value);
    const name = parseCellString(row.getCell(3).value);
    const quantity = parseCellNumber(row.getCell(4).value);

    if (symbol) {
      stocks.push({ symbol, name, quantity });
    }
  });

  return stocks;
};

// Parse trade confirmation Excel file (handles both BUY and SELL sheets)
export const parseTradeConfirmation = async (file: File): Promise<TradeConfirmationRow[]> => {
  if (file.name.toLowerCase().endsWith(".csv")) {
    const text = await file.text();
    const csvRows = parseCsvTextRows(text);
    if (csvRows.length === 0) return [];

    const header = csvRows[0].map((cell) => cell.toLowerCase().trim());
    const tradeDateCol = header.findIndex((cell) => cell === "trade date");
    const settlementDateCol = header.findIndex((cell) => cell === "settlement date");
    const nameCol = header.findIndex((cell) => cell === "security name");
    const symbolCol = header.findIndex((cell) => cell === "security symbol");
    const instructionTypeCol = header.findIndex((cell) => cell === "instruction type");
    const portfolioClassCol = header.findIndex((cell) => cell.includes("potfolio class") || cell.includes("portfolio class"));
    const quantityCol = header.findIndex((cell) => cell.includes("quantity"));
    const unitPriceCol = header.findIndex((cell) => cell === "unit price");
    const secAmountCol = header.findIndex((cell) => cell.includes("sec amount total"));
    const usdCol = header.findIndex((cell) => cell === "usd");
    const typeCol = header.findIndex((cell) => cell === "type");

    const rows: TradeConfirmationRow[] = [];
    let refCounter = 1;
    csvRows.slice(1).forEach((raw) => {
      const symbol = (raw[symbolCol] || "").trim();
      if (!symbol || symbol.length > 10) return;
      const quantity = parseFloat((raw[quantityCol] || "").replace(/,/g, "")) || 0;
      const unitPrice = parseFloat((raw[unitPriceCol] || "").replace(/,/g, "")) || 0;
      if (quantity <= 0 || unitPrice <= 0) return;

      rows.push({
        refNo: refCounter++,
        tradeDate: (raw[tradeDateCol] || "").trim(),
        settlementDate: (raw[settlementDateCol] || "").trim(),
        name: (raw[nameCol] || "").trim(),
        symbol,
        instructionType: (raw[instructionTypeCol] || "").trim() || "DVP",
        portfolioClass: (raw[portfolioClassCol] || "").trim() || "Equity",
        quantity,
        unitPrice,
        secAmountTotal: ceilTo2Decimals(parseFloat((raw[secAmountCol] || "").replace(/,/g, "")) || 0),
        fee: (raw[typeCol] || "").trim() || "0.30%",
        feeAmount: 0,
        totalAmount: ceilTo2Decimals(parseFloat((raw[secAmountCol] || "").replace(/,/g, "")) || 0),
        currency: "USD",
        usdRate: parseFloat((raw[usdCol] || "").replace(/,/g, "")) || 0,
      });
    });

    return rows;
  }

  const workbook = new ExcelJS.Workbook();
  const buffer = await file.arrayBuffer();
  await workbook.xlsx.load(buffer);

  const rows: TradeConfirmationRow[] = [];

  workbook.worksheets.forEach((worksheet) => {
    let csvStyleHeaderRow = 0;
    let tradeDateCol = 0;
    let settlementDateCol = 0;
    let nameCol = 0;
    let symbolCol = 0;
    let instructionTypeCol = 0;
    let portfolioClassCol = 0;
    let quantityCol = 0;
    let unitPriceCol = 0;
    let secAmountCol = 0;
    let usdCol = 0;
    let typeCol = 0;

    const maxHeaderScanRows = Math.min(10, worksheet.rowCount || 10);
    for (let rowNumber = 1; rowNumber <= maxHeaderScanRows; rowNumber++) {
      const row = worksheet.getRow(rowNumber);
      const headerCells: string[] = [];
      for (let col = 1; col <= Math.max(worksheet.columnCount || 0, 20); col++) {
        headerCells[col - 1] = parseCellString(row.getCell(col).value).toLowerCase().trim();
      }

      const maybeTradeDateCol = headerCells.findIndex((cell) => cell === "trade date");
      const maybeSettlementDateCol = headerCells.findIndex((cell) => cell === "settlement date");
      const maybeNameCol = headerCells.findIndex((cell) => cell === "security name");
      const maybeSymbolCol = headerCells.findIndex((cell) => cell === "security symbol");
      const maybeQuantityCol = headerCells.findIndex((cell) => cell.includes("quantity"));
      const maybeUnitPriceCol = headerCells.findIndex((cell) => cell === "unit price");

      if (
        maybeTradeDateCol >= 0 &&
        maybeSettlementDateCol >= 0 &&
        maybeNameCol >= 0 &&
        maybeSymbolCol >= 0 &&
        maybeQuantityCol >= 0 &&
        maybeUnitPriceCol >= 0
      ) {
        csvStyleHeaderRow = rowNumber;
        tradeDateCol = maybeTradeDateCol + 1;
        settlementDateCol = maybeSettlementDateCol + 1;
        nameCol = maybeNameCol + 1;
        symbolCol = maybeSymbolCol + 1;
        quantityCol = maybeQuantityCol + 1;
        unitPriceCol = maybeUnitPriceCol + 1;
        instructionTypeCol = headerCells.findIndex((cell) => cell === "instruction type") + 1;
        portfolioClassCol = headerCells.findIndex((cell) => cell.includes("potfolio class") || cell.includes("portfolio class")) + 1;
        secAmountCol = headerCells.findIndex((cell) => cell.includes("sec amount total")) + 1;
        usdCol = headerCells.findIndex((cell) => cell === "usd") + 1;
        typeCol = headerCells.findIndex((cell) => cell === "type") + 1;
        break;
      }
    }

    if (csvStyleHeaderRow > 0) {
      let refCounter = rows.length + 1;
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber <= csvStyleHeaderRow) return;

        const symbol = parseCellString(row.getCell(symbolCol).value);
        if (!symbol || symbol.length > 10) return;

        const quantity = parseCellNumber(row.getCell(quantityCol).value);
        const unitPrice = parseCellNumber(row.getCell(unitPriceCol).value);
        if (quantity <= 0 || unitPrice <= 0) return;

        const secAmountTotal = secAmountCol > 0
          ? ceilTo2Decimals(parseCellNumber(row.getCell(secAmountCol).value))
          : ceilTo2Decimals(quantity * unitPrice);

        rows.push({
          refNo: refCounter++,
          tradeDate: parseCellString(row.getCell(tradeDateCol).value),
          settlementDate: parseCellString(row.getCell(settlementDateCol).value),
          name: parseCellString(row.getCell(nameCol).value),
          symbol,
          instructionType: instructionTypeCol > 0 ? (parseCellString(row.getCell(instructionTypeCol).value) || "DVP") : "DVP",
          portfolioClass: portfolioClassCol > 0 ? (parseCellString(row.getCell(portfolioClassCol).value) || "Equity") : "Equity",
          quantity,
          unitPrice,
          secAmountTotal,
          fee: typeCol > 0 ? (parseCellString(row.getCell(typeCol).value) || "0.30%") : "0.30%",
          feeAmount: 0,
          totalAmount: secAmountTotal,
          currency: "USD",
          usdRate: usdCol > 0 ? parseCellNumber(row.getCell(usdCol).value) : 0,
        });
      });
      return;
    }

    let dataStartRow = 0;
    let foundHeader = false;

    worksheet.eachRow((row, rowNumber) => {
      const cell1 = parseCellString(row.getCell(1).value).toLowerCase();
      const cell2 = parseCellString(row.getCell(2).value).toLowerCase();

      if (!foundHeader && (cell1.includes("sender reference") || cell1 === "1" && cell2.includes("/"))) {
        foundHeader = true;
        if (/^\d+$/.test(parseCellString(row.getCell(1).value))) {
          dataStartRow = rowNumber;
        } else {
          dataStartRow = rowNumber + 1;
        }
      }
    });

    if (!foundHeader) {
      worksheet.eachRow((row, rowNumber) => {
        const cell1 = parseCellString(row.getCell(1).value);
        const cell5 = parseCellString(row.getCell(5).value);
        if (!foundHeader && cell1 === "1" && cell5.length >= 2 && cell5.length <= 6 && /^[A-Z]+$/.test(cell5)) {
          foundHeader = true;
          dataStartRow = rowNumber;
        }
      });
    }

    if (!foundHeader) return;

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber < dataStartRow) return;

      const refNoStr = parseCellString(row.getCell(1).value);
      const symbol = parseCellString(row.getCell(5).value);

      if (!refNoStr || refNoStr.toLowerCase().includes("нийт") || refNoStr.toLowerCase() === "total") return;
      if (!symbol || symbol.length > 10) return;

      const refNo = parseInt(refNoStr) || 0;
      if (refNo === 0) return;

      const tradeDate = parseCellString(row.getCell(2).value);
      const settlementDate = parseCellString(row.getCell(3).value);
      const name = parseCellString(row.getCell(4).value);
      const instructionType = parseCellString(row.getCell(6).value) || "DVP";
      const portfolioClass = parseCellString(row.getCell(7).value) || "Equity";
      const quantity = parseCellNumber(row.getCell(8).value);
      const unitPrice = parseCellNumber(row.getCell(9).value);
      const secAmountTotal = ceilTo2Decimals(parseCellNumber(row.getCell(10).value));
      const fee = parseCellString(row.getCell(11).value) || "0.30%";
      const feeAmount = ceilTo2Decimals(parseCellNumber(row.getCell(12).value));
      const totalAmount = ceilTo2Decimals(parseCellNumber(row.getCell(13).value));
      const currency = parseCellString(row.getCell(14).value) || "USD";

      rows.push({
        refNo,
        tradeDate,
        settlementDate,
        name,
        symbol,
        instructionType,
        portfolioClass,
        quantity,
        unitPrice,
        secAmountTotal,
        fee,
        feeAmount,
        totalAmount,
        currency,
        usdRate: 0,
      });
    });
  });

  return rows;
};

// Parse detailed withdraw request file
// Detects column layout automatically by finding email column
export const parseDetailedWithdrawRequest = async (file: File): Promise<DetailedWithdrawRequestRow[]> => {
  const workbook = new ExcelJS.Workbook();
  const buffer = await file.arrayBuffer();
  await workbook.xlsx.load(buffer);

  const rows: DetailedWithdrawRequestRow[] = [];
  const seen = new Set<string>();

  workbook.worksheets.forEach((worksheet) => {
    // Find a real header row (must explicitly contain an email-like header).
    let headerRowIdx = 0;
    let emailColIdx = 0;
    const maxScanRows = Math.min(30, worksheet.rowCount || 30);
    const maxScanCols = 30;

    for (let r = 1; r <= maxScanRows && emailColIdx === 0; r++) {
      const headerRow = worksheet.getRow(r);
      for (let col = 1; col <= maxScanCols; col++) {
        const cellValue = parseCellString(headerRow.getCell(col).value).toLowerCase().trim();
        if (
          cellValue === "email" ||
          cellValue === "и-мейл" ||
          cellValue === "э-мэйл" ||
          cellValue.includes("email") ||
          cellValue.includes("мейл")
        ) {
          headerRowIdx = r;
          emailColIdx = col;
          break;
        }
      }
    }

    // Skip worksheets that do not have a recognizable header row.
    if (emailColIdx <= 0 || headerRowIdx <= 0) return;

    // Column offsets based on email column position
    // Expected: Email, Phone, LastName, FirstName, RegNum, TTD, AssetType, AssetName, Qty, TotalPrice, Interest, UCKFee, AvtaFee, Tax, PurchaseDate, TransferAmt
    const offset = emailColIdx - 1;

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber <= headerRowIdx) return; // Skip header

      const email = parseCellString(row.getCell(1 + offset).value);
      if (!email || !email.includes("@")) return;

      // Parse purchase date - handle Excel date format
      let purchaseDate = parseCellString(row.getCell(15 + offset).value);
      const rawDate = row.getCell(15 + offset).value;
      if (typeof rawDate === "number" && isExcelDate(rawDate)) {
        purchaseDate = excelDateToString(rawDate);
      }

      const regNumber = parseCellString(row.getCell(5 + offset).value).trim();
      if (!regNumber) return;

      const assetName = parseCellString(row.getCell(8 + offset).value);
      const quantity = parseCellNumber(row.getCell(9 + offset).value);
      const dedupeKey = `${email.toLowerCase().trim()}|${regNumber}|${assetName}|${purchaseDate}|${quantity}`;
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);

      rows.push({
        email,
        phone: parseCellString(row.getCell(2 + offset).value),
        lastName: parseCellString(row.getCell(3 + offset).value),
        firstName: parseCellString(row.getCell(4 + offset).value),
        regNumber,
        ttd: parseCellString(row.getCell(6 + offset).value),
        assetType: parseCellString(row.getCell(7 + offset).value),
        assetName,
        quantity,
        totalPrice: parseCellNumber(row.getCell(10 + offset).value),
        interest: parseCellNumber(row.getCell(11 + offset).value),
        uckFee: parseCellNumber(row.getCell(12 + offset).value),
        avtamataFee: parseCellNumber(row.getCell(13 + offset).value),
        tax: parseCellNumber(row.getCell(14 + offset).value),
        purchaseDate,
        transferAmount: parseCellNumber(row.getCell(16 + offset).value),
      });
    });
  });

  // Debug: Log first parsed row
  if (rows.length > 0) {
    console.log("First parsed row:", JSON.stringify(rows[0], null, 2));
  }

  return rows;
};

// Parse purchase history file (accumulative rows)
export const parsePurchaseHistory = async (file: File): Promise<PurchaseHistoryRow[]> => {
  const workbook = new ExcelJS.Workbook();
  const buffer = await file.arrayBuffer();
  await workbook.xlsx.load(buffer);

  const rows: PurchaseHistoryRow[] = [];
  const normalizeWorksheetName = (value: string) =>
    String(value || "")
      .toLowerCase()
      .replace(/[\s_-]+/g, "");
  const yieldToBrowser = () =>
    new Promise<void>((resolve) => {
      setTimeout(resolve, 0);
    });
  const stockSymbols = new Set([
    "DIA", "VDE", "VUG", "VXF", "BITI", "TSLA", "BABA", "SGOV", "PDD", "MPNGY", "FXI",
    "JD", "HIMS", "VAL", "NVDA", "HOOD", "LI", "OSCR", "DVN", "CDE", "EUO", "BCI", "XLE",
  ]);

  const preferredWorksheet =
    workbook.worksheets.find(
      (worksheet) => normalizeWorksheetName(worksheet.name) === "sheet1"
    ) ||
    workbook.worksheets.find((worksheet) =>
      normalizeWorksheetName(worksheet.name).includes("sheet1")
    );
  const worksheetsToParse = preferredWorksheet
    ? [preferredWorksheet]
    : workbook.worksheets;

  for (const worksheet of worksheetsToParse) {
    const startingRowCount = rows.length;
    let headerRowIdx = 0;
    // Detect column layout from header
    let emailCol = 6; // Default: И-мейл
    let dateCol = 1;
    let lastNameCol = 2;
    let firstNameCol = 3;
    let regNumberCol = 4;
    let phoneCol = 5;
    let txCodeCol = 0;
    let amountCol = 7; // Мөнгөн дүн
    let portfolioIndexCol = 8; // Багц №
    let bondCol = 9; // Bond
    let holdAmountCol = 10; // Битүүмжлэгдсэн дүн
    const stockAllocationCols: Record<string, number> = {};

    const headerSearchLimit = Math.min(worksheet.rowCount, 25);
    for (let rowNumber = 1; rowNumber <= headerSearchLimit; rowNumber++) {
      const row = worksheet.getRow(rowNumber);
      const maxCols = Math.max(
        worksheet.actualColumnCount || 0,
        worksheet.columnCount || 0,
        row.cellCount || 0,
        40,
      );
      const rowValues = Array.from({ length: maxCols }, (_, index) =>
        parseCellString(row.getCell(index + 1).value).toLowerCase().trim(),
      );
      const cell1 = rowValues[0] || "";
      const looksLikeHeader =
        cell1.includes("огноо") ||
        cell1.includes("date") ||
        cell1.includes("ха") ||
        (
          rowValues.some((val) => val.includes("мейл") || val.includes("email")) &&
          rowValues.some((val) => val.includes("мөнгөн дүн") || val === "amount") &&
          rowValues.some((val) => val.includes("багц") || val === "bond")
        );

      if (looksLikeHeader) {
        headerRowIdx = rowNumber;
        // Detect columns from header
        for (let col = 1; col <= maxCols; col++) {
          const val = rowValues[col - 1];
          const upperVal = val.toUpperCase();
          if (val.includes("мейл") || val.includes("email")) emailCol = col;
          if (val.includes("мөнгөн дүн") || val === "amount") amountCol = col;
          if (val.includes("багц")) portfolioIndexCol = col;
          if (val === "bond") bondCol = col;
          if (val.includes("битүүм")) holdAmountCol = col;
          if (val.includes("овог")) lastNameCol = col;
          if (val.includes("нэр") && !val.includes("овог")) firstNameCol = col;
          if (val.includes("регистр")) regNumberCol = col;
          if (val.includes("утас") || val.includes("phone")) phoneCol = col;
          if (
            val.includes("гүйлгээний код") ||
            val.includes("гүйлгээний утга") ||
            val.includes("deposit code") ||
            val === "txcode" ||
            val === "tx code"
          ) {
            txCodeCol = col;
          }
          if (stockSymbols.has(upperVal)) {
            const nextVal = rowValues[col] || "";
            stockAllocationCols[upperVal] = nextVal.includes("тоо") ? col + 1 : col;
          }
        }
        break;
      }
    }

    if (headerRowIdx === 0) {
      headerRowIdx = 1;
    }

    for (let rowNumber = headerRowIdx + 1; rowNumber <= worksheet.rowCount; rowNumber++) {
      const row = worksheet.getRow(rowNumber);

      const email = sanitizeEmail(parseCellString(row.getCell(emailCol).value));
      if (!email || !email.includes("@")) {
        continue;
      }

      let haDate = parseCellString(row.getCell(dateCol).value);
      const rawDate = row.getCell(dateCol).value;
      if (rawDate instanceof Date) {
        const m = rawDate.getUTCMonth() + 1;
        const d = rawDate.getUTCDate();
        const y = rawDate.getUTCFullYear();
        haDate = `${m}/${d}/${y}`;
      } else if (typeof rawDate === "number" && isExcelDate(rawDate)) {
        haDate = excelDateToString(rawDate);
      }

      const stockAllocations = Object.entries(stockAllocationCols).reduce<Record<string, number>>((acc, [symbol, col]) => {
        const value = parseCellNumber(row.getCell(col).value);
        if (value !== 0) acc[symbol] = value;
        return acc;
      }, {});

      rows.push({
        haDate,
        lastName: parseCellString(row.getCell(lastNameCol).value),
        firstName: parseCellString(row.getCell(firstNameCol).value),
        regNumber: parseCellString(row.getCell(regNumberCol).value).trim().toUpperCase(),
        phone: parseCellString(row.getCell(phoneCol).value).trim(),
        email,
        txCode: txCodeCol > 0 ? (parseCellString(row.getCell(txCodeCol).value) || "") : "",
        amount: parseCellNumber(row.getCell(amountCol).value),
        portfolioIndex: parseCellNumber(row.getCell(portfolioIndexCol).value),
        bond: parseCellNumber(row.getCell(bondCol).value),
        holdAmount: parseCellNumber(row.getCell(holdAmountCol).value),
        stockAllocations,
      });

      if (rowNumber % 1000 === 0) {
        await yieldToBrowser();
      }
    }

    // Purchase history uploads are expected to come from a single worksheet.
    // Some workbooks carry helper/hidden tabs; reading all of them introduces
    // stray Sheet 5 rows that do not exist in the user's actual source sheet.
    if (rows.length > startingRowCount) {
      break;
    }
  }

  return rows;
};

// Parse bank statement file
// Bank statement structure:
// Column A (1): Огноо (Date)
// Column B (2): Дансны дугаар
// Column C (3): Валют
// Column D (4): Зарлага
// Column E (5): Орлого (Income/Amount - Мөнгөн дүн)
// Column F (6): Үлдэгдэл
// Column G (7): Гүйлгээний утга (Transaction code like "AT1-4CE9-388H")
export const parseBankStatement = async (file: File): Promise<BankStatementRow[]> => {
  const workbook = new ExcelJS.Workbook();
  const buffer = await file.arrayBuffer();
  await workbook.xlsx.load(buffer);

  const rows: BankStatementRow[] = [];

  workbook.worksheets.forEach((worksheet) => {
    // Detect column layout from header row (row 1)
    const headerRow = worksheet.getRow(1);
    let dateCol = 1;    // Огноо / Голомт
    let incomeCol = 7;  // ДТ/Орлого
    let txCodeCol = 9;  // Гүйлгээний код
    let accountCol = 10; // Account number after txCode

    // Scan header to find correct columns
    for (let col = 1; col <= 20; col++) {
      const val = parseCellString(headerRow.getCell(col).value).toLowerCase().trim();
      if (val.includes("дт") || val.includes("орлого")) incomeCol = col;
      if (val.includes("гүйлгээний код") || val.includes("гүйлгээний утга")) txCodeCol = col;
    }
    accountCol = txCodeCol + 1;

    // Fallback: scan first few data rows to find AT1 column
    if (txCodeCol <= 1) {
      for (let r = 2; r <= Math.min(20, worksheet.rowCount); r++) {
        const dataRow = worksheet.getRow(r);
        for (let col = 1; col <= 15; col++) {
          const val = parseCellString(dataRow.getCell(col).value);
          if (val.toUpperCase().includes("AT1-")) {
            txCodeCol = col;
            incomeCol = col - 2; // ДТ/Орлого is 2 before txCode
            accountCol = col + 1;
            break;
          }
        }
        if (txCodeCol > 1) break;
      }
    }

    console.log(`Bank statement "${worksheet.name}": dateCol=${dateCol}, incomeCol=${incomeCol}, txCodeCol=${txCodeCol}, accountCol=${accountCol}`);

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return; // Skip header

      const income = parseCellNumber(row.getCell(incomeCol).value);
      if (income <= 0) return;

      const txCode = parseCellString(row.getCell(txCodeCol).value);

      // Column A (1): Огноо (Date)
      let dateVal = parseCellString(row.getCell(dateCol).value);
      const rawDate = row.getCell(dateCol).value;
      if (typeof rawDate === "number" && isExcelDate(rawDate)) {
        dateVal = excelDateToString(rawDate);
      }

      const account = parseCellString(row.getCell(accountCol).value);

      rows.push({
        date: dateVal,
        income,
        txCode,
        account,
      });
    });
  });

  console.log(`Parsed ${rows.length} bank statement rows`);
  if (rows.length > 0) {
    console.log("Sample bank statement row:", rows[0]);
  }

  // Debug: Find rows with txCode
  const rowsWithTxCode = rows.filter(r => r.txCode && r.txCode.length > 0);
  console.log(`Rows with txCode: ${rowsWithTxCode.length}`);
  if (rowsWithTxCode.length > 0) {
    console.log("Sample rows with txCode:", rowsWithTxCode.slice(0, 3));
  }

  return rows;
};

// Parse sold stock file (accumulative sheets)
export const parseSoldStock = async (file: File): Promise<{ name: string; data: SoldStockRow[] }[]> => {
  const workbook = new ExcelJS.Workbook();
  const buffer = await file.arrayBuffer();
  await workbook.xlsx.load(buffer);

  const sheets: { name: string; data: SoldStockRow[] }[] = [];
  const normalizeHeader = (value: ExcelJS.CellValue): string =>
    parseCellString(value)
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();
  const findHeaderCol = (headers: string[], patterns: string[], fallback: number): number => {
    const idx = headers.findIndex((header) =>
      patterns.some((pattern) => header === pattern || header.includes(pattern)),
    );
    return idx >= 0 ? idx + 1 : fallback;
  };
  const formatDateValue = (value: ExcelJS.CellValue): string => {
    if (value instanceof Date) {
      const m = value.getUTCMonth() + 1;
      const d = value.getUTCDate();
      const y = value.getUTCFullYear();
      return `${m}/${d}/${y}`;
    }
    if (typeof value === "number") {
      return excelDateToString(value);
    }
    if (value && typeof value === "object") {
      if ("result" in value) {
        return formatDateValue(value.result as ExcelJS.CellValue);
      }
      if ("text" in value && typeof value.text === "string") {
        return value.text.trim();
      }
      if ("richText" in value && Array.isArray(value.richText)) {
        return value.richText.map((part) => part.text || "").join("").trim();
      }
    }
    return parseCellString(value);
  };

  workbook.worksheets.forEach((worksheet) => {
    const sheetData: SoldStockRow[] = [];

    let headerRowIdx = 0;
    worksheet.eachRow((row, rowNumber) => {
      const cell1 = parseCellString(row.getCell(1).value).toLowerCase();
      if (cell1 === "email" || cell1 === "и-мейл" || cell1 === "э-мэйл" || cell1.includes("email") || cell1.includes("мейл")) {
        headerRowIdx = rowNumber;
        return;
      }
    });

    // If no header found, try checking other columns for email header
    if (headerRowIdx === 0) {
      worksheet.eachRow((row, rowNumber) => {
        if (headerRowIdx > 0) return;
        for (let col = 2; col <= 10; col++) {
          const cellVal = parseCellString(row.getCell(col).value).toLowerCase();
          if (cellVal === "email" || cellVal.includes("email") || cellVal.includes("мейл")) {
            headerRowIdx = rowNumber;
            return;
          }
        }
      });
    }

    // If still no header found, assume row 1 is header and data starts from row 2
    if (headerRowIdx === 0) {
      console.warn(`Sheet "${worksheet.name}": No email header found, assuming row 1 is header`);
      headerRowIdx = 1;
    }

    const headerCells: string[] = [];
    const headerRow = worksheet.getRow(headerRowIdx);
    headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      headerCells[colNumber - 1] = normalizeHeader(cell.value);
    });

    const emailCol = findHeaderCol(headerCells, ["email", "и-мейл", "э-мэйл", "мейл"], 1);
    const phoneCol = findHeaderCol(headerCells, ["утас", "phone"], 2);
    const lastNameCol = findHeaderCol(headerCells, ["овог", "lastname", "last name"], 3);
    const firstNameCol = findHeaderCol(headerCells, ["нэр", "firstname", "first name"], 4);
    const regNumberCol = findHeaderCol(headerCells, ["регистр", "регистер", "reg"], 5);
    const ttdCol = findHeaderCol(headerCells, ["ттд", "ttd"], 6);
    const assetTypeCol = findHeaderCol(headerCells, ["хөрөнгийн төрөл", "asset type"], 7);
    const assetNameCol = findHeaderCol(headerCells, ["хөрөнгийн нэр", "asset name"], 8);
    const quantityCol = findHeaderCol(headerCells, ["тоо ширхэг", "quantity"], 9);
    const priceCol = findHeaderCol(headerCells, ["ханш", "price"], 10);
    const usdMntCol = findHeaderCol(headerCells, ["usd-mnt", "usd/mnt", "usd mnt", "usd"], 11);
    const totalPriceCol = findHeaderCol(headerCells, ["нийт үнэ", "total price", "total"], 12);
    const uckFeeCol = findHeaderCol(headerCells, ["үцк шимтгэл", "үкц шимтгэл", "ucc fee", "uck fee"], 13);
    const avtamataFeeCol = findHeaderCol(headerCells, ["avtamata шимтгэл", "автамата шимтгэл", "avtamata fee"], 14);
    const transferAmountCol = findHeaderCol(headerCells, ["шилжүүлэх дүн", "шилжүүлэх", "transfer amount"], 15);
    const dateCol = findHeaderCol(headerCells, ["огноо", "date"], 16);

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber <= headerRowIdx) return;

      const email = parseCellString(row.getCell(emailCol).value);
      if (!email || !email.includes("@")) return;

      const dateVal = formatDateValue(row.getCell(dateCol).value);

      sheetData.push({
        email,
        phone: parseCellString(row.getCell(phoneCol).value),
        lastName: parseCellString(row.getCell(lastNameCol).value),
        firstName: parseCellString(row.getCell(firstNameCol).value),
        regNumber: parseCellString(row.getCell(regNumberCol).value),
        ttd: parseCellString(row.getCell(ttdCol).value),
        assetType: parseCellString(row.getCell(assetTypeCol).value),
        assetName: parseCellString(row.getCell(assetNameCol).value),
        quantity: parseCellNumber(row.getCell(quantityCol).value),
        price: parseCellNumber(row.getCell(priceCol).value),
        usdMnt: parseCellNumber(row.getCell(usdMntCol).value),
        totalPrice: parseCellNumber(row.getCell(totalPriceCol).value),
        uckFee: parseCellNumber(row.getCell(uckFeeCol).value),
        avtamataFee: parseCellNumber(row.getCell(avtamataFeeCol).value),
        transferAmount: parseCellNumber(row.getCell(transferAmountCol).value),
        date: dateVal,
        description: worksheet.name,
      });
    });

    if (sheetData.length > 0) {
      sheets.push({ name: worksheet.name, data: sheetData });
    }
  });

  return sheets;
};

// Parse bond metadata from CSV text (transposed layout: bonds in columns)
export const parseBondMetadataFromCSV = (csvText: string): BondMetadataRow[] => {
  const lines = csvText.split("\n");

  let nameRow: string[] = [];
  let rateRow: string[] = [];
  let frequencyRow: string[] = [];
  let maturityRow: string[] = [];
  const paymentDates: string[][] = [];

  lines.forEach((line) => {
    const cells = line.split(",").map(c => c.trim().replace(/"/g, ""));
    const firstCell = cells[0].toLowerCase();

    if (firstCell === "name") {
      nameRow = cells.slice(1);
    } else if (firstCell === "fixed coupon rate") {
      rateRow = cells.slice(1);
    } else if (firstCell === "coupon frequency") {
      frequencyRow = cells.slice(1);
    } else if (firstCell.includes("maturity date") || firstCell === "maturity date") {
      maturityRow = cells.slice(1);
    } else if (firstCell.includes("payment date")) {
      paymentDates.push(cells.slice(1));
    }
  });

  const bondMetadata: BondMetadataRow[] = [];

  nameRow.forEach((name, idx) => {
    if (!name) return;

    let rate = 0;
    const rateStr = rateRow[idx] || "";
    if (rateStr.includes("%")) {
      rate = parseFloat(rateStr.replace("%", "")) / 100;
    } else {
      rate = parseFloat(rateStr) || 0;
    }

    const frequency = frequencyRow[idx] || "quarterly";

    const dates: string[] = [];
    paymentDates.forEach(row => {
      if (row[idx]) dates.push(row[idx]);
    });

    bondMetadata.push({
      bondName: name,
      rate,
      frequency: frequency.toLowerCase(),
      paymentDates: dates,
      maturityDate: maturityRow[idx] || "",
    });
  });

  return bondMetadata;
};

// Parse bond metadata file (supports both CSV and Excel)
export const parseBondMetadata = async (file: File): Promise<BondMetadataRow[]> => {
  const fileName = file.name.toLowerCase();

  // Handle CSV files - use transposed layout parser
  if (fileName.endsWith(".csv")) {
    const text = await file.text();
    return parseBondMetadataFromCSV(text);
  }

  // Handle Excel files
  const workbook = new ExcelJS.Workbook();
  const buffer = await file.arrayBuffer();
  await workbook.xlsx.load(buffer);

  const worksheet = workbook.worksheets[0];

  // Detect if Excel uses transposed layout (first column has labels like "Name", "Fixed Coupon Rate")
  let isTransposed = false;
  worksheet.eachRow((row) => {
    const cell1 = parseCellString(row.getCell(1).value).toLowerCase();
    if (cell1 === "name" || cell1 === "fixed coupon rate") {
      isTransposed = true;
    }
  });

  if (isTransposed) {
    // Convert worksheet to CSV text and reuse CSV parser
    const lines: string[] = [];
    worksheet.eachRow((row) => {
      const cells: string[] = [];
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        while (cells.length < colNumber - 1) cells.push("");
        cells.push(parseCellString(cell.value));
      });
      lines.push(cells.join(","));
    });
    return parseBondMetadataFromCSV(lines.join("\n"));
  }

  // Row-based layout: Column A = bond name, Column B = rate, Column C = frequency
  const rows: BondMetadataRow[] = [];

  let headerRowIdx = 0;
  worksheet.eachRow((row, rowNumber) => {
    const cell1 = parseCellString(row.getCell(1).value).toLowerCase();
    if (cell1.includes("бонд") || cell1.includes("нэр") || cell1.includes("name")) {
      headerRowIdx = rowNumber;
      return;
    }
  });

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber <= headerRowIdx) return;

    const bondName = parseCellString(row.getCell(1).value);
    let rate = parseCellNumber(row.getCell(2).value);

    if (!bondName || rate === 0) return;

    if (rate > 1) rate = rate / 100;

    rows.push({
      bondName,
      rate,
      frequency: parseCellString(row.getCell(3).value) || "quarterly",
      paymentDates: [],
    });
  });

  return rows;
};

// Parse charge file (accumulative sheets)
export const parseCharge = async (file: File): Promise<{ name: string; data: ChargeRow[] }[]> => {
  const workbook = new ExcelJS.Workbook();
  const buffer = await file.arrayBuffer();
  await workbook.xlsx.load(buffer);

  const sheets: { name: string; data: ChargeRow[] }[] = [];

  workbook.worksheets.forEach((worksheet) => {
    const sheetData: ChargeRow[] = [];
    let defaultHaDate = worksheet.name;

    if (/^\d{8}$/.test(worksheet.name)) {
      defaultHaDate = `${worksheet.name.substring(0, 4)}/${worksheet.name.substring(4, 6)}/${worksheet.name.substring(6, 8)}`;
    }

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;

      const email = sanitizeEmail(parseCellString(row.getCell(6).value));
      if (!email) return;

      // Read date from column 10 if available, otherwise use sheet name
      const dateCell = row.getCell(10).value;
      let haDate = defaultHaDate;
      if (dateCell) {
        // Handle different date formats
        if (dateCell instanceof Date) {
          haDate = dateCell.toISOString();
        } else if (typeof dateCell === 'string') {
          haDate = dateCell;
        } else if (typeof dateCell === 'number') {
          // Excel serial number - convert to date
          const excelEpoch = new Date(1899, 11, 30);
          const millisPerDay = 24 * 60 * 60 * 1000;
          const date = new Date(excelEpoch.getTime() + dateCell * millisPerDay);
          haDate = date.toISOString();
        }
      }

      sheetData.push({
        index: parseCellNumber(row.getCell(1).value) || rowNumber - 1,
        lastName: parseCellString(row.getCell(2).value),
        firstName: parseCellString(row.getCell(3).value),
        regNumber: parseCellString(row.getCell(4).value),
        phone: parseCellString(row.getCell(5).value),
        email,
        portfolioIndex: parseCellNumber(row.getCell(7).value), // Багц № - column G
        amount: parseCellNumber(row.getCell(8).value), // Мөнгөн дүн - column H
        description: parseCellString(row.getCell(9).value), // Тайлбар - column I
        haDate,
      });
    });

    if (sheetData.length > 0) {
      sheets.push({ name: worksheet.name, data: sheetData });
    }
  });

  return sheets;
};

const parseExcelDateLike = (value: ExcelJS.CellValue): string => {
  if (!value) return "";
  if (value instanceof Date) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  if (typeof value === "number" && isExcelDate(value)) {
    const normalized = excelDateToString(value);
    const match = normalized.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (match) {
      return `${match[3]}-${String(Number(match[1])).padStart(2, "0")}-${String(Number(match[2])).padStart(2, "0")}`;
    }
    return normalized;
  }

  const text = parseCellString(value).trim().replace(/\.0+$/, "");
  if (!text) return "";

  const ymd = text.match(/^(\d{4})[./-](\d{1,2})[./-](\d{1,2})$/);
  if (ymd) {
    return `${ymd[1]}-${String(Number(ymd[2])).padStart(2, "0")}-${String(Number(ymd[3])).padStart(2, "0")}`;
  }
  const mdy = text.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (mdy) {
    return `${mdy[3]}-${String(Number(mdy[1])).padStart(2, "0")}-${String(Number(mdy[2])).padStart(2, "0")}`;
  }
  return text;
};

const inferDistributionBondName = (
  workbook: ExcelJS.Workbook,
  fileName: string,
): string => {
  const bondNameVotes = new Map<string, number>();
  workbook.worksheets.forEach((worksheet) => {
    let productColumn = 0;
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 10) return;
      row.eachCell((cell, colNumber) => {
        const value = parseCellString(cell.value).toLowerCase();
        if (
          value.includes("бүтээгдэхүүний нэр") ||
          value.includes("product") ||
          value.includes("bond name")
        ) {
          productColumn = colNumber;
        }
      });
    });
    if (!productColumn) return;

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;
      const productName = normalizeBondName(parseCellString(row.getCell(productColumn).value));
      if (!productName) return;
      bondNameVotes.set(productName, (bondNameVotes.get(productName) || 0) + 1);
    });
  });

  let bestBondName = "";
  let bestCount = -1;
  bondNameVotes.forEach((count, bondName) => {
    if (count > bestCount) {
      bestCount = count;
      bestBondName = bondName;
    }
  });
  if (bestBondName) return bestBondName;

  return normalizeBondName(fileName.replace(/\.[^.]+$/, ""));
};

export const parseBondDistribution = async (file: File): Promise<BondDistributionFile> => {
  const workbook = new ExcelJS.Workbook();
  const buffer = await file.arrayBuffer();
  await workbook.xlsx.load(buffer);

  const bondName = inferDistributionBondName(workbook, file.name);
  const rows: BondDistributionRow[] = [];
  const normalizeHeader = (value: string) =>
    value
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();
  const PREFERRED_SHEET_NAMES = [
    "буцаалт тархалт - dashboard",
    "тархалт бэлтгэх",
  ];
  const findHeaderColumns = (worksheet: ExcelJS.Worksheet) => {
    let headerRowIdx = 0;
    let regCol = 0;
    let amountCol = 0;
    let purchaseDateCol = 0;
    let interestEndDateCol = 0;
    let daysCol = 0;
    let rateCol = 0;
    let interestCol = 0;
    let taxCol = 0;
    let lastNameCol = 0;
    let firstNameCol = 0;

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 15 || headerRowIdx) return;
      const headerMap = new Map<number, string>();
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        headerMap.set(
          colNumber,
          normalizeHeader(parseCellString(cell.value))
        );
      });
      const values = Array.from(headerMap.values());
      const hasRegister = values.includes("регистр");
      const hasAmount = values.includes("мөнгөн дүн");
      const hasStartDate =
        values.includes("худалдан авсан огноо") ||
        values.includes("хүү тооцсон огноо");
      const hasEndDate =
        values.includes("хүү тооцох огноо");
      if (
        !hasRegister ||
        !hasAmount ||
        !hasStartDate ||
        !hasEndDate
      ) {
        return;
      }

      headerRowIdx = rowNumber;
      headerMap.forEach((value, colNumber) => {
        if (value === "овог") lastNameCol = colNumber;
        if (value === "нэр") firstNameCol = colNumber;
        if (value === "регистр") regCol = colNumber;
        if (value === "мөнгөн дүн") amountCol = colNumber;
        if (
          value === "худалдан авсан огноо" ||
          value === "хүү тооцсон огноо"
        ) {
          purchaseDateCol = colNumber;
        }
        if (value === "хүү тооцох огноо") {
          interestEndDateCol = colNumber;
        }
        if (value === "хүү тооцсон хоног") {
          daysCol = colNumber;
        }
        if (value === "жилийн хүү") {
          rateCol = colNumber;
        }
        if (value === "тооцсон хүү") {
          interestCol = colNumber;
        }
        if (value === "ххоат") {
          taxCol = colNumber;
        }
      });
    });

    if (!headerRowIdx || !regCol || !purchaseDateCol || !interestEndDateCol) {
      return null;
    }

    return {
      worksheet,
      headerRowIdx,
      regCol,
      amountCol,
      purchaseDateCol,
      interestEndDateCol,
      daysCol,
      rateCol,
      interestCol,
      taxCol,
      lastNameCol,
      firstNameCol,
    };
  };

  const preferredWorksheet =
    workbook.worksheets.find((worksheet) =>
      PREFERRED_SHEET_NAMES.includes(
        normalizeHeader(worksheet.name)
      )
    ) ||
    workbook.worksheets.find((worksheet) =>
      normalizeHeader(worksheet.name) ===
      "тархалт бэлтгэх"
    );
  const targetHeader =
    preferredWorksheet &&
    findHeaderColumns(preferredWorksheet);

  if (targetHeader) {
    const {
      worksheet,
      headerRowIdx,
      regCol,
      amountCol,
      purchaseDateCol,
      interestEndDateCol,
      daysCol,
      rateCol,
      interestCol,
      taxCol,
      lastNameCol,
      firstNameCol,
    } = targetHeader;

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber <= headerRowIdx) return;
      const regNumber = parseCellString(row.getCell(regCol).value).trim().toUpperCase();
      if (!regNumber) return;

      rows.push({
        bondName,
        lastName: lastNameCol ? parseCellString(row.getCell(lastNameCol).value) : "",
        firstName: firstNameCol ? parseCellString(row.getCell(firstNameCol).value) : "",
        regNumber,
        amount: amountCol ? parseCellNumber(row.getCell(amountCol).value) : 0,
        purchaseDate: parseExcelDateLike(row.getCell(purchaseDateCol).value),
        interestEndDate: parseExcelDateLike(row.getCell(interestEndDateCol).value),
        days: daysCol ? parseCellNumber(row.getCell(daysCol).value) : 0,
        rate: rateCol ? parseCellNumber(row.getCell(rateCol).value) : 0,
        calculatedInterest: interestCol ? parseCellNumber(row.getCell(interestCol).value) : 0,
        tax: taxCol ? parseCellNumber(row.getCell(taxCol).value) : 0,
      });
    });
  }

  const dedupedRows = rows
    .filter((row) => row.regNumber && row.purchaseDate && row.interestEndDate)
    .filter((row, index, allRows) => {
      const key = [
        row.regNumber,
        row.purchaseDate,
        row.interestEndDate,
        row.amount,
      ].join("|");
      return allRows.findIndex((candidate) =>
        [
          candidate.regNumber,
          candidate.purchaseDate,
          candidate.interestEndDate,
          candidate.amount,
        ].join("|") === key
      ) === index;
    });

  return {
    fileName: file.name,
    bondName,
    data: dedupedRows,
  };
};

// Parse generic Excel file with headers (for per-customer files)
export const parseGenericExcel = async (file: File): Promise<Record<string, unknown>[]> => {
  const workbook = new ExcelJS.Workbook();
  const buffer = await file.arrayBuffer();
  await workbook.xlsx.load(buffer);

  const worksheet = workbook.worksheets[0];
  const rows: Record<string, unknown>[] = [];

  const headerRow = worksheet.getRow(1);
  const headers: string[] = [];
  headerRow.eachCell((cell, colNumber) => {
    headers[colNumber] = String(cell.value || "").trim();
  });

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;

    const rowData: Record<string, unknown> = {};
    row.eachCell((cell, colNumber) => {
      const header = headers[colNumber];
      if (header) {
        rowData[header] = cell.value;
      }
    });

    if (Object.keys(rowData).length > 0) {
      rows.push(rowData);
    }
  });

  return rows;
};

// Combine trade confirmations and remove duplicates
export const combineTradeConfirmations = (files: TradeConfirmationRow[][]): TradeConfirmationRow[] => {
  const combined: Map<string, TradeConfirmationRow> = new Map();
  let refCounter = 1;

  files.flat().forEach((row) => {
    const key = [
      row.symbol,
      row.tradeDate,
      row.settlementDate,
      row.quantity,
      row.unitPrice,
      row.usdRate || 0,
    ].join("|");
    if (!combined.has(key)) {
      combined.set(key, { ...row, refNo: refCounter++ });
    }
  });

  return Array.from(combined.values());
};
